import { useCallback, useEffect, useState } from "react";
import type { FlagKindName, FlagSeverityName, PatientFlag } from "@dentacore/shared-types";
import { FLAG_KINDS, FLAG_SEVERITIES } from "@dentacore/shared-types";

import { createFlag, listFlags, reopenFlag, resolveFlag } from "../api/patients";
import { Modal } from "../ui/modal";
import styles from "./flags-modal.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * Recording what somebody has to know before they treat this patient.
 *
 * ── Why this exists (QA #37) ────────────────────────────────────────────────
 *
 * The flag endpoints have worked since STORY-019 and **nothing had ever called
 * them.** Flags rendered in the patient header, drove the calendar's critical
 * treatment and fed Home's *Watch today* — all of it reading rows only the seed
 * could write. A practice starting from an empty database could not record a
 * penicillin allergy through the application at all.
 *
 * ── Resolve, never delete ───────────────────────────────────────────────────
 *
 * The API has no delete and this offers none. A flag that stops being true is
 * *resolved*, which keeps it on the record and lets it be reopened — "this was
 * never true" and "this is no longer true" are different facts about a patient,
 * and only the second is something a clinical record should be able to say.
 *
 * Resolved flags stay listed, greyed, below the active ones. They are the ones
 * most worth seeing when somebody asks why a warning went away.
 *
 * ── Why a modal rather than a tab ───────────────────────────────────────────
 *
 * Flags belong to the header — they are what the whole file is read against —
 * and the four tabs are the record itself. Putting them in a fifth tab would
 * bury the one thing that has to be visible before anybody starts working.
 */
export function FlagsModal({
  patientId,
  onClose,
  onChanged,
}: {
  patientId: string;
  onClose: () => void;
  /** The header's chips come from the file payload, so it has to be re-read. */
  onChanged: () => void;
}): React.JSX.Element {
  const [flags, setFlags] = useState<PatientFlag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [kind, setKind] = useState<FlagKindName>("ALLERGY");
  const [severity, setSeverity] = useState<FlagSeverityName>("CRITICAL");
  const [label, setLabel] = useState("");
  const [detail, setDetail] = useState("");

  const reload = useCallback(
    (signal?: AbortSignal) =>
      listFlags(patientId, signal)
        .then(setFlags)
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          setError(cause instanceof Error ? cause.message : "Could not read this patient's flags.");
        }),
    [patientId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  /** Every write follows the same path: run it, re-read, tell the file. */
  const run = (action: () => Promise<unknown>): void => {
    setBusy(true);
    setError(null);
    action()
      .then(() => reload())
      .then(onChanged)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That change did not save.");
      })
      .finally(() => setBusy(false));
  };

  const canAdd = label.trim() !== "" && !busy;

  const active = (flags ?? []).filter((flag) => flag.isActive);
  const resolved = (flags ?? []).filter((flag) => !flag.isActive);

  return (
    <Modal label="Flags and alerts" onClose={onClose}>
      <header className={styles.header}>
        <h2 className={styles.title}>Flags and alerts</h2>
        <button type="button" className={styles.close} onClick={onClose} data-testid="flags-close">
          Close
        </button>
      </header>

      <p className={styles.intro}>
        What anybody treating this patient has to know first. Critical flags are drawn on their
        appointments and counted on the dashboard.
      </p>

      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (!canAdd) return;
          run(() =>
            createFlag(patientId, {
              kind,
              severity,
              label: label.trim(),
              ...(detail.trim() === "" ? {} : { detail: detail.trim() }),
            }),
          );
          setLabel("");
          setDetail("");
        }}
      >
        <div className={styles.formRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Kind
              <RequiredMark />
            </span>
            <select
              className={styles.select}
              value={kind}
              onChange={(event) => setKind(event.target.value as FlagKindName)}
              data-testid="flag-kind"
            >
              {FLAG_KINDS.map((value) => (
                <option key={value} value={value}>
                  {titleOf(value)}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Severity
              <RequiredMark />
            </span>
            <select
              className={styles.select}
              value={severity}
              onChange={(event) => setSeverity(event.target.value as FlagSeverityName)}
              data-testid="flag-severity"
            >
              {FLAG_SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {titleOf(value)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            Flag
            <RequiredMark />
          </span>
          <input
            className={styles.input}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Penicillin allergy"
            maxLength={120}
            data-testid="flag-label"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Detail</span>
          <input
            className={styles.input}
            value={detail}
            onChange={(event) => setDetail(event.target.value)}
            // The consequence, not a restatement of the label — this is what
            // the header chip shows on hover and what actually changes what
            // somebody does.
            placeholder="What to do instead — e.g. clindamycin is this file's alternative"
            maxLength={500}
            data-testid="flag-detail"
          />
        </label>

        <button type="submit" className={styles.add} disabled={!canAdd} data-testid="flag-add">
          Add flag
        </button>
      </form>

      {error !== null && (
        <p className={styles.error} data-testid="flags-error">
          {error}
        </p>
      )}

      {flags === null ? (
        <p className={styles.empty}>Reading…</p>
      ) : flags.length === 0 ? (
        <p className={styles.empty} data-testid="flags-empty">
          Nothing flagged on this patient.
        </p>
      ) : (
        <ul className={styles.list} data-testid="flags-list">
          {[...active, ...resolved].map((flag) => (
            <li
              key={flag.id}
              className={`${styles.row} ${flag.isActive ? "" : styles.rowResolved}`}
              data-testid="flag-row"
              data-active={flag.isActive ? "true" : "false"}
            >
              <div className={styles.rowMain}>
                <span
                  className={`${styles.chip} ${
                    flag.severity === "CRITICAL" && flag.isActive ? styles.chipCritical : ""
                  }`}
                >
                  {titleOf(flag.kind)}
                </span>
                <span className={styles.rowLabel}>{flag.label}</span>
                {flag.detail !== null && <span className={styles.rowDetail}>{flag.detail}</span>}
                {/*
                  **Its earlier wording, on itself** (PP-D11): a changed alert is
                  never silently a different alert.
                */}
                {(flag.history ?? []).map((earlier) => (
                  <span
                    key={earlier.changedAt}
                    className={styles.rowEarlier}
                    data-testid="flag-earlier"
                  >
                    Was {earlier.label ?? flag.label}
                    {earlier.detail !== null && ` — ${earlier.detail}`}
                    {earlier.changedBy !== null && ` · changed by ${earlier.changedBy}`}
                  </span>
                ))}
              </div>

              <button
                type="button"
                className={styles.action}
                disabled={busy}
                onClick={() =>
                  run(() =>
                    flag.isActive
                      ? resolveFlag(patientId, flag.id)
                      : reopenFlag(patientId, flag.id),
                  )
                }
                data-testid={flag.isActive ? "flag-resolve" : "flag-reopen"}
              >
                {flag.isActive ? "Resolve" : "Reopen"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/** `FRONT_DESK` → `Front desk`. Micro-labels are sentence case, never uppercase. */
function titleOf(value: string): string {
  const lower = value.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
